import React, { useState } from 'react'
import { AccountPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import { GetSSOProviderRequest } from '@trustero/trustero-api-web/lib/account/account_pb'
import { Navigate, useLocation } from 'react-router-dom'
import { useCognitoContext } from 'src/context/FormContext/CognitoContext'
import { getCognitoAuthGrant } from 'src/Utils/oidc'
import { isValidEmail } from 'src/Utils/validators'
import {
  StandardButtonSize,
  StandardButtonVariant,
} from 'src/components/Reusable/Buttons'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { TextInput } from 'src/components/Reusable/Inputs'
import { NTRCE_API_URL } from '../../../adapter/gRpcAdapter'
import {
  LoginPageContainer,
  LoginPageContent,
  LoginPageGrid,
  TrusteroLine,
  TrusteroLogo,
} from '../LoginPage.styles'
import { OIDCEmailButton, OIDCEmailText } from './OIDCEmailPage.styles'

export const OIDCEmailPage = (): JSX.Element => {
  const location = useLocation()
  const { initialCognitoAuth } = useCognitoContext()
  const [dirty, setDirty] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [email, setEmail] = useState<string>('')
  const [error, setError] = useState<string>('')

  if (!initialCognitoAuth) {
    return <Navigate to={location.state?.from || '/login'} replace />
  }

  if (isLoading) {
    return (
      <LoginPageContainer>
        <LoginPageGrid>
          <LoginPageContent>
            <OIDCEmailText>Loading...</OIDCEmailText>
          </LoginPageContent>
        </LoginPageGrid>
      </LoginPageContainer>
    )
  }

  const onSubmit = async () => {
    setDirty(true)
    if (!isValidEmail(email)) {
      setError('Please enter a valid email')
      return
    }
    setIsLoading(true)
    try {
      const client = new AccountPromiseClient(NTRCE_API_URL)
      const req = new GetSSOProviderRequest().setEmail(email)
      const resp = await client.getSSOProvider(req)
      getCognitoAuthGrant({
        ...initialCognitoAuth,
        provider: resp.getProvider(),
      })
    } catch {
      setError('Provider not found. Please reach out to support@trustero.com')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <LoginPageContainer>
      <LoginPageGrid>
        <LoginPageContent>
          <TrusteroLogo width="100%" height="100%" />
          <TrusteroLine />
          <OIDCEmailText>
            Enter your email address to continue logging in with{' '}
            {initialCognitoAuth.provider}
          </OIDCEmailText>
          <FlexRow gap={16} align={FlexAlign.CENTER}>
            <TextInput
              label="Email"
              name="email"
              initVal={email}
              onChangeCb={(value) => {
                setDirty(true)
                setError('')
                setEmail(value)
              }}
              placeholder="Please enter your email"
              required
              errorMessage={error}
              isValid={!dirty || isValidEmail(email)}
            />
            <OIDCEmailButton
              buttonSize={StandardButtonSize.MEDIUM}
              variant={StandardButtonVariant.SECONDARY}
              onClick={onSubmit}
            >
              Continue
            </OIDCEmailButton>
          </FlexRow>
        </LoginPageContent>
      </LoginPageGrid>
    </LoginPageContainer>
  )
}
